import Button from "@/components/button/Button.tsx";
import {DatePickerMode, DatePickerValue} from "@/components/datePicker/DatePicker.types.ts";
import {DatePickerCalendarProps} from "@/components/datePicker/subComponents/DatePickerCalendar/DatePickerCalendar.types.ts";
import {DatePickerPopoverInput} from "@/components/datePicker/subComponents/DatePickerCalendar/subComponents/DatePickerPopoverInput/DatePickerPopoverInput.tsx";
import {DatePickerTimeSelect} from "@/components/datePicker/subComponents/DatePickerCalendar/subComponents/DatePickerTimeSelect/DatePickerTimeSelect.tsx";
import {DatePickerDayPicker} from "@/components/datePicker/subComponents/DatePickerDayPicker/DatePickerDayPicker.tsx";
import {DatePickerShortcutList} from "@/components/datePicker/subComponents/DatePickerShortcutList/DatePickerShortcutList.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {useMediaQuery} from "@/lib/hooks/useMediaQuery.ts";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {DateFormatName} from "@/lib/types/common.ts";
import {getCurrentTimezone} from "@/lib/types/datetime.ts";
import {MouseEvent, useEffect, useLayoutEffect, useRef, useState} from "react";
import {DateRange} from "react-day-picker";
import styles from "./DatePickerCalendar.module.scss";

export const DatePickerCalendar = <Mode extends DatePickerMode = "range">({
  timezone,
  hasTimeSelect,
  timeIntervalMinutes = 15,
  mode = "range" as Mode,
  hasPopoverInput,
  onReset,
  onCancel,
  onDone,
  onChange,
  dateFormat = DateFormatName.DEFAULT,
  timeFormat,
  disableFutureDates,
  disablePastDates,
  selected: selectedProp,
  splitRole,
  fixedRange: fixedRangeProp,
  name = "date-picker-calendar",
}: DatePickerCalendarProps<Mode>) => {
  if (!timezone) {
    console.warn("Timezone is not set, using local timezone");
    timezone = getCurrentTimezone();
  }

  const [previousSelected, setPreviousSelected] = useState<DatePickerValue<Mode>>(
    selectedProp as DatePickerValue<Mode>
  );
  const [selected, setSelected] = useState<DatePickerValue<Mode>>(selectedProp as DatePickerValue<Mode>);

  const [fixedRange, setFixedRange] = useState<StandardDateRange | undefined>(fixedRangeProp);
  const [prevFixedRange, setPrevFixedRange] = useState<StandardDateRange | undefined>(fixedRangeProp);

  const isMobile = useMediaQuery("(max-width: 767px)");

  const calendarRef = useRef<HTMLDivElement>(null);
  const [calendarHeight, setCalendarHeight] = useState<number | undefined>(346); // Default height based on 5 row month

  const setSelectedAndOnChange = (value: DatePickerValue<Mode>, fixedRange?: StandardDateRange) => {
    setSelected(value);
    setFixedRange(fixedRange);
    onChange?.(value, fixedRange);
  };

  // This is used to set the height of the picker to the calendar height so that when the time select or shortcuts are shown they are set to the height of the calendar
  useLayoutEffect(() => {
    if (!calendarRef.current) return;
    const handleResize = () => {
      setCalendarHeight(calendarRef.current?.offsetHeight);
    };
    handleResize();
    const observer = new window.ResizeObserver(handleResize);
    observer.observe(calendarRef.current);
    return () => observer.disconnect();
  }, [selected]);

  useEffect(() => {
    setSelected(selectedProp as DatePickerValue<Mode>);
    setPreviousSelected(selectedProp as DatePickerValue<Mode>);
  }, [selectedProp]);

  useEffect(() => {
    setFixedRange(fixedRangeProp);
    setPrevFixedRange(fixedRangeProp);
  }, [fixedRangeProp]);

  return (
    <div className={styles.datePickerCalendarContainer}>
      <div className={styles.datePickerBody}>
        <div className={styles.datePickerRow} style={{height: isMobile ? undefined : calendarHeight}}>
          {mode === "range" && (
            <>
              <div className={styles.datePickerShortcutList}>
                <DatePickerShortcutList
                  onChange={(start: Date, end: Date, fixedRange) => {
                    setSelectedAndOnChange({from: start, to: end} as DatePickerValue<Mode>, fixedRange);
                  }}
                  timezone={timezone}
                  fixedRange={fixedRange}
                />
              </div>
              <Divider
                orientation={isMobile ? "horizontal" : "vertical"}
                className={styles.datePickerDividerShortcut}
              />
            </>
          )}
          <div ref={calendarRef} className={styles.datePickerCalendar}>
            {hasPopoverInput && (
              <DatePickerPopoverInput
                mode={mode}
                name={name}
                timezone={timezone}
                selected={selected}
                dateFormat={dateFormat}
                timeFormat={timeFormat}
                splitRole={splitRole}
                setSelectedAndOnChange={setSelectedAndOnChange}
              />
            )}
            <DatePickerDayPicker
              disabled={
                disableFutureDates && disablePastDates
                  ? [{after: new Date()}, {before: new Date()}]
                  : disableFutureDates
                    ? {after: new Date()}
                    : disablePastDates
                      ? {before: new Date()}
                      : undefined
              }
              mode={mode}
              selected={selected}
              onChange={(date: Date | DateRange | undefined) => {
                setSelectedAndOnChange(date as DatePickerValue<Mode>);
                setFixedRange(undefined);
              }}
              timeZone={timezone}
              splitRole={splitRole}
            />
          </div>
          {hasTimeSelect && (
            <DatePickerTimeSelect
              mode={mode}
              timezone={timezone}
              disableFutureDates={disableFutureDates}
              disablePastDates={disablePastDates}
              timeIntervalMinutes={timeIntervalMinutes}
              splitRole={splitRole}
              selected={selected}
              setSelectedAndOnChange={setSelectedAndOnChange}
            />
          )}
        </div>
      </div>
      <div className={styles.datePickerFooter}>
        <Button variant={"ghost"} onClick={(e: MouseEvent<HTMLButtonElement>) => onReset?.(e)}>
          Reset
        </Button>

        <div className={"d-flex gap-2"}>
          {mode === "range" && (
            <Button
              onClick={(e) => {
                setSelectedAndOnChange(previousSelected, prevFixedRange);
                onCancel?.(e);
              }}
              variant={"secondary"}
            >
              Cancel
            </Button>
          )}
          <Button onClick={(e) => onDone?.(e)}>Done</Button>
        </div>
      </div>
    </div>
  );
};
